import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button, Select, Textarea } from "components/ui";
import { ACTIVITY_STATUS } from "constants/activityStatus.constant";
import { toast } from "sonner";

const ActivityStatusChangeModal = ({ isOpen, onClose, count, onStatusChange, initialStatusId }) => {
  const [formData, setFormData] = useState({
    statusId: "",
    remarks: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const saveRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        statusId: initialStatusId ? String(initialStatusId) : "",
        remarks: "",
      });
    }
  }, [isOpen, initialStatusId]);

  const resetForm = () => {
    setFormData({
      statusId: "",
      remarks: "",
    });
    setIsSubmitting(false);
  };

  const handleStatusChange = (e) => {
    const statusId = e.target.value;
    setFormData(prev => ({
      ...prev,
      statusId,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.statusId) {
      toast.info("Please select a status");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onStatusChange({
        statusId: parseInt(formData.statusId, 10),
        remarks: formData.remarks,
      });
      
      if (result !== false) {
        resetForm();
        onClose();
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment} afterLeave={resetForm}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={()=>{}}
        initialFocus={saveRef}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-lg origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none">
            <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 dark:bg-dark-800 sm:px-5">
              <DialogTitle
                as="h3"
                className="flex items-center gap-2 text-base font-medium text-gray-800 dark:text-dark-100"
              >
                <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                Change Activity Status
              </DialogTitle>
              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            <div className="flex flex-col overflow-y-auto px-4 py-4 sm:px-5">
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                Update the status for {count} selected {count === 1 ? 'activity' : 'activities'}.
              </p>
              
              <div className="space-y-5">
                <Select 
                  label="Status *"
                  value={formData.statusId}
                  onChange={handleStatusChange}
                  disabled={isSubmitting}
                >
                  <option value="">Select Status</option>
                  {ACTIVITY_STATUS.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </Select>

                <Textarea
                  label="Remarks (Optional)"
                  value={formData.remarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="Add remarks about this status change..."
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="mt-6 space-x-3 text-end">
                <Button
                  onClick={onClose}
                  variant="outlined"
                  className="min-w-[7rem] rounded-full"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  color="primary"
                  ref={saveRef}
                  className="min-w-[7rem] rounded-full"
                  disabled={isSubmitting || !formData.statusId}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

export default ActivityStatusChangeModal;
